import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

// Client-facing Bliq ride consent page (EN/ET). Opened from the WhatsApp/email
// invite. The client picks a ride: a demo ride, a drop-off nearby, or a short
// round trip (car waits 5-10 min and brings them back) — route details are
// arranged with the safety driver on the spot. Any yes-choice consents to
// sharing name/email with Bliq (ws5 then auto-emails the Bliq team).
// URL /:decision only auto-acts for "decline" (1-tap opt-out); "agree" links
// from older invites land on this form so the client makes an explicit choice.
const API = 'https://api.wiseclean.ee/api:wc-bliq';

const RIDE_CHOICES = ['demo', 'destination_oneway', 'destination_return'];

const C = {
  bg: '#E8F7FA', card: '#FFFFFF', cyan: '#6DCBDE', cyanDark: '#1A7D8E',
  text: '#1A1A2E', muted: '#6B7280', border: '#E2E8F0', error: '#EF4444',
};
const S = {
  page: { minHeight: '100vh', background: C.bg, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px', fontFamily: 'DM Sans, Arial, sans-serif', color: C.text },
  card: { background: C.card, borderRadius: 16, padding: '28px 24px', maxWidth: 460, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  h1: { fontSize: 22, fontWeight: 700, margin: '0 0 14px' },
  body: { fontSize: 15, lineHeight: 1.55, whiteSpace: 'pre-line', color: C.text },
  when: { fontSize: 14, fontWeight: 600, color: C.cyanDark, background: C.bg, borderRadius: 10, padding: '10px 12px', margin: '0 0 14px' },
  note: { fontSize: 13, color: C.muted, marginTop: 16, padding: '10px 12px', background: C.bg, borderRadius: 10 },
  noteLink: { color: C.cyanDark },
  more: { display: 'inline-block', marginTop: 12, color: C.cyanDark, fontSize: 14, textDecoration: 'none' },
  agree: { width: '100%', padding: '14px', borderRadius: 8, border: 'none', background: C.cyan, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginTop: 18 },
  alt: { width: '100%', padding: '13px', borderRadius: 8, border: `1px solid ${C.cyan}`, background: '#fff', color: C.cyanDark, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 10 },
  decline: { width: '100%', padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.muted, fontSize: 14, cursor: 'pointer', marginTop: 10 },
  disabled: { opacity: 0.6, cursor: 'default' },
  err: { color: C.error, fontSize: 14, marginTop: 10 },
};

export default function BliqConsent() {
  const { lang, token, decision } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState('loading'); // loading|form|thanks|declined|resolved|expired|error
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [when, setWhen] = useState(null);
  const respondedRef = useRef(false);

  useEffect(() => {
    if (['en', 'et'].includes(lang)) i18n.changeLanguage(lang);
    else { navigate(`/external/bliq-consent/en/${token}`, { replace: true }); return; }
  }, [lang, token, i18n, navigate]);

  useEffect(() => {
    if (!token) { setState('error'); return; }
    let cancelled = false;
    fetch(`${API}/consent_by_token?consent_token=${encodeURIComponent(token)}`)
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((d) => {
        if (cancelled) return;
        setWhen({ date: d.when_date, time: d.when_time });
        if (d.expired) { setState('expired'); return; }
        if (d.resolved) { setState('resolved'); return; }
        // Only a decline carried in the URL (WhatsApp/email button) is recorded
        // without the form — 1-tap opt-out, guarded against double render.
        // An /agree link opens the form so the client picks the ride type.
        if (decision === 'decline') {
          if (!respondedRef.current) { respondedRef.current = true; respond('decline'); }
          return;
        }
        setState('form');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [token, decision]);

  async function respond(choice) {
    setSubmitting(true);
    setErr('');
    try {
      const r = await fetch(`${API}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_token: token,
          agree: choice !== 'decline',
          ride_choice: choice,
          locale: lang,
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        throw new Error((b && b.message) || t('bliqConsentError'));
      }
      // The endpoint is idempotent: an already-resolved request (stale tab,
      // second device) returns 200 with the CURRENT status and records nothing.
      // Trust the returned status, not the local button, so a client never sees
      // a false "thank you, the ride is being arranged".
      const body = await r.json().catch(() => null);
      const serverStatus = body && body.status;
      const expected = choice === 'decline' ? 'declined' : 'consented';
      if (serverStatus && serverStatus !== expected) { setState('resolved'); return; }
      setState(choice === 'decline' ? 'declined' : 'thanks');
    } catch (e) {
      setErr(e.message);
      setState((s) => (s === 'loading' ? 'form' : s));
    } finally {
      setSubmitting(false);
    }
  }

  let content;
  if (state === 'loading') content = <p style={{ color: C.muted }}>{t('bliqConsentLoading')}</p>;
  else if (state === 'error') content = (<><h1 style={S.h1}>{t('bliqConsentErrorTitle')}</h1><p style={{ color: C.muted }}>{t('bliqConsentError')}</p></>);
  else if (state === 'expired') content = (<><h1 style={S.h1}>{t('bliqConsentExpiredTitle')}</h1><p style={{ color: C.muted }}>{t('bliqConsentExpired')}</p></>);
  else if (state === 'resolved') content = (<><h1 style={S.h1}>{t('bliqConsentResolvedTitle')}</h1><p style={{ color: C.muted }}>{t('bliqConsentResolvedBody')}</p></>);
  else if (state === 'thanks') content = (<><h1 style={S.h1}>{t('bliqConsentThanksTitle')}</h1><p style={{ color: C.muted }}>{t('bliqConsentThanksBody')}</p></>);
  else if (state === 'declined') content = (<><h1 style={S.h1}>{t('bliqConsentDeclinedTitle')}</h1><p style={{ color: C.muted }}>{t('bliqConsentDeclinedBody')}</p></>);
  else content = (
    <>
      <h1 style={S.h1}>{t('bliqConsentTitle')}</h1>
      {when && when.date ? <p style={S.when}>{when.time ? t('bliqConsentWhen', { date: when.date, time: when.time }) : t('bliqConsentWhenNoTime', { date: when.date })}</p> : null}
      <p style={S.body}>{t('bliqConsentBody')}</p>
      <a style={S.more} href="https://bliq.ai" target="_blank" rel="noreferrer">{t('bliqConsentMore')} →</a>
      <div style={S.note}>
        {t('bliqConsentShareNote')}{' '}
        <a style={S.noteLink} href="https://bliq.ai/privacy" target="_blank" rel="noreferrer">{t('bliqConsentPrivacy')}</a>
      </div>
      {err && <div style={S.err}>{err}</div>}
      <button style={{ ...S.agree, ...(submitting ? S.disabled : {}) }} onClick={() => respond(RIDE_CHOICES[0])} disabled={submitting}>
        {t('bliqConsentAgreeDemo')}
      </button>
      <button style={{ ...S.alt, ...(submitting ? S.disabled : {}) }} onClick={() => respond(RIDE_CHOICES[1])} disabled={submitting}>
        {t('bliqConsentAgreeDropoff')}
      </button>
      <button style={{ ...S.alt, ...(submitting ? S.disabled : {}) }} onClick={() => respond(RIDE_CHOICES[2])} disabled={submitting}>
        {t('bliqConsentAgreeReturn')}
      </button>
      <button style={{ ...S.decline, ...(submitting ? S.disabled : {}) }} onClick={() => respond('decline')} disabled={submitting}>
        {t('bliqConsentDecline')}
      </button>
    </>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>{content}</div>
    </div>
  );
}
